import { Link, useSearchParams } from "@remix-run/react";
import type { LoaderFunctionArgs } from "@remix-run/node";
import {
  AuthButton,
  AuthField,
  AuthInput,
  AuthLink,
  AuthNote,
  AuthShell,
} from "~app/components/v13/auth";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ROUTES } from "~server/routes";
import { handleAuthLoader } from "~app/utils/auth-helpers";
import { useRequestPasswordReset } from "~app/hooks/use-password-reset";
import { useState } from "react";
import type { RequestPasswordResetRequest } from "~server/dto/password";
import { requestPasswordResetSchema } from "~server/dto/password";
import { RESET_LINK_PROBLEM_MESSAGE } from "~app/utils/auth-messages";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  return handleAuthLoader({ request });
};

export default function ForgotPasswordPage() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const requestPasswordReset = useRequestPasswordReset();
  // reset-password sends a bad or expired link here as ?error=… — say so,
  // instead of landing on a blank form with no explanation
  const [searchParams] = useSearchParams();
  const resetLinkProblem = searchParams.get("error") !== null;

  const {
    formState: { errors },
    setError,
    register,
    handleSubmit,
  } = useForm<RequestPasswordResetRequest>({
    resolver: zodResolver(requestPasswordResetSchema),
    mode: "onTouched",
    reValidateMode: "onChange",
  });

  const onSubmit = async (data: RequestPasswordResetRequest) => {
    requestPasswordReset.mutate(
      {
        email: data.email,
      },
      {
        onSuccess: () => {
          setIsSubmitted(true);
        },
        onError: (error) => {
          setError("root", { message: error.message });
        },
      }
    );
  };

  const foot = (
    <div className="space-y-2">
      <Link prefetch="intent" to={ROUTES.LOGIN}>
        <AuthLink>← Back to sign in</AuthLink>
      </Link>
      <div>
        New here?{" "}
        <Link prefetch="intent" to={ROUTES.REGISTER}>
          <AuthLink>Create an account</AuthLink>
        </Link>
      </div>
    </div>
  );

  // ⛔ THE SAME ANSWER WHETHER THE ACCOUNT EXISTS OR NOT. Telling someone "no
  // such email" turns this form into a way to find out who has an account here.
  if (isSubmitted) {
    return (
      <AuthShell
        title="Check your email"
        sub="If an account exists for that address, a reset link is on its way. The link is good for one use."
        foot={foot}
      >
        <AuthNote tone="pine">
          Nothing arrived? Check the spam folder, then try again — the address
          may be spelled differently from the one on the account.
        </AuthNote>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Forgot your password?"
      sub="Give us the address on the account and we will send a link to set a new one."
      foot={foot}
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {resetLinkProblem ? (
          <AuthNote>{RESET_LINK_PROBLEM_MESSAGE}</AuthNote>
        ) : null}
        {errors.root ? <AuthNote>{errors.root.message}</AuthNote> : null}

        <AuthField label="Email address" error={errors.email?.message ?? null}>
          <AuthInput
            id="email"
            type="email"
            autoComplete="email"
            placeholder="you@company.com"
            invalid={Boolean(errors.email)}
            {...register("email")}
          />
        </AuthField>

        <AuthButton type="submit" disabled={requestPasswordReset.isPending}>
          {requestPasswordReset.isPending ? "Sending…" : "Send the reset link"}
        </AuthButton>
      </form>
    </AuthShell>
  );
}
